import { Box, Button, Container, Grid, Card, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader, Page } from '../../../components';
import { useToastContext } from '../../../hooks';
import { FileUploader } from '../../../components/file/file-uploader';
import { Save } from '@mui/icons-material';
import { ConfirmationDialogue } from '../../../components/modal/ConfirmationDialogue';
import { makeStyles } from '@mui/styles';
import { theme } from '../../../styles';
import { createFinancials } from '../../../fetch/imports';

export const ImportFinancials: FC = () => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const { showToast } = useToastContext();
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [errorsList, setErrorsList] = useState<string[] | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successfulRecords, setSuccessfulRecords] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const classes = useStyles();

  const handleSubmit = async () => {
    if (!uploadedFile || !propertyId) return;

    setIsSubmitting(true);
    try {
      const res = await createFinancials(parseInt(propertyId, 10), uploadedFile);
      if (res?.Detail) {
        showToast('error', `Error, ${res.Detail}`);
        setErrorsList(res.Detail.split('\r\n'));
        return;
      } else if (res?.errors?.length > 0 || res?.validationErrors?.length > 0) {
        setErrorsList(
          (res.validationErrors ?? []).concat(res.errors ?? []).map((error: string) => `${error}\r\n`)
        );
        return;
      }
      setSuccessfulRecords(res?.successCount ?? 0);
      setShowSuccess(true);
      setUploadedFile(null);
    } catch (error: any) {
      console.log(error);
      showToast('error', 'Error importing financial data, please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Page title="Import Financials">
      <Container maxWidth={false} disableGutters>
        <Card className={classes.uploader} elevation={2}>
          <Typography variant="subtitle1" gutterBottom>
            Property ID: {propertyId}
          </Typography>
          <Grid container>
            <Grid container item spacing={1} xs={12} sm={6}>
              <Grid item xs={12}>
                <FileUploader
                  handleFile={val => {
                    setUploadedFile(val);
                  }}
                  handleRemove={() => {
                    setUploadedFile(null);
                  }}
                  removeText={'Remove'}
                  templateFile={uploadedFile?.name ?? ''}
                  isRequired={true}
                  dropzoneText="Financial Data File (.xlsx or .csv)"
                />
              </Grid>
            </Grid>
          </Grid>
          <Box marginTop="2rem">
            <Button
              disabled={!uploadedFile || isSubmitting}
              onClick={handleSubmit}
              startIcon={<Save />}
              color="secondary"
              variant="contained"
            >
              Upload
            </Button>
          </Box>
          {errorsList && (
            <ConfirmationDialogue
              open
              id="errors-financials"
              title="Upload Errors"
              text={errorsList?.join('\r\n')}
              hideCancel
              onClose={() => {
                setErrorsList(null);
                setUploadedFile(null);
              }}
              onConfirm={() => {
                setErrorsList(null);
                setUploadedFile(null);
              }}
            />
          )}
          {showSuccess && (
            <ConfirmationDialogue
              open
              id="success-financials"
              title="Upload Success"
              text={`Successfully imported ${successfulRecords} rows`}
              hideCancel
              onClose={() => {
                setShowSuccess(false);
                setSuccessfulRecords(0);
              }}
              onConfirm={() => {
                setShowSuccess(false);
                setSuccessfulRecords(0);
              }}
            />
          )}
          {isSubmitting && (
            <Loader position="centered" type="fullscreen">
              Importing Financial Data ...
            </Loader>
          )}
        </Card>
      </Container>
    </Page>
  );
};

const useStyles = makeStyles(() => ({
  uploader: {
    padding: theme.spacing(2),
  },
}));
